import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  XCircle,
  User,
  Hash,
  MessageSquare,
  CalendarPlus,
  ArrowRight,
  Search,
  Eye,
  EyeOff,
  Shield,
  Camera,
  Download
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { formatUK } from '../../lib/ukTime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupervisorTeamClockPage() {
  const navigate = useNavigate();
  const [employeeCode, setEmployeeCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [comments, setComments] = useState({});
  const [clockingEmployee, setClockingEmployee] = useState(null);
  const [availableShiftsCount, setAvailableShiftsCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleEmails, setVisibleEmails] = useState({});
  const [photoModal, setPhotoModal] = useState({ open: false, photo: null, name: '', timestamp: null, loading: false });
  
  // Photo capture states for clock-in/out
  const [captureModal, setCaptureModal] = useState({ 
    open: false, 
    employee: null, 
    eventType: null, // 'clock_in' or 'clock_out'
    photo: null,
    useCode: false // true if using employee code input
  });

  useEffect(() => {
    fetchEmployees();
    fetchAvailableShiftsCount();
    // Auto-sync shifts
    axios.post(`${API}/admin/sync-shifts`).catch(() => {});
  }, []);

  const fetchEmployees = async () => {
    try {
      const [empRes, supRes] = await Promise.all([
        axios.get(`${API}/supervisor/employees`),
        axios.get(`${API}/supervisor/supervisors`)
      ]);
      setEmployees(empRes.data);
      setSupervisors(supRes.data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  const fetchAvailableShiftsCount = async () => {
    try {
      const response = await axios.get(`${API}/open-shifts`);
      const available = response.data.filter(s => s.status === 'available');
      setAvailableShiftsCount(available.length);
    } catch (error) {
      console.error('Failed to fetch available shifts');
    }
  };

  const handleCommentChange = (employeeId, value) => {
    setComments(prev => ({
      ...prev,
      [employeeId]: value
    }));
  };

  // Handle photo capture for file input
  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCaptureModal(prev => ({ ...prev, photo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Open capture modal for code-based clock in
  const handleClockIn = async () => {
    if (!employeeCode || employeeCode.length !== 4) {
      toast.error('Please enter a valid 4-digit employee code');
      return;
    }
    // Open photo capture modal
    setCaptureModal({
      open: true,
      employee: null,
      eventType: 'clock_in',
      photo: null,
      useCode: true
    });
  };

  // Open capture modal for code-based clock out
  const handleClockOut = async () => {
    if (!employeeCode || employeeCode.length !== 4) {
      toast.error('Please enter a valid 4-digit employee code');
      return;
    }
    // Open photo capture modal
    setCaptureModal({
      open: true,
      employee: null,
      eventType: 'clock_out',
      photo: null,
      useCode: true
    });
  };

  // Execute clock in/out with photo
  const executeClockWithPhoto = async () => {
    if (!captureModal.photo) {
      toast.error('Please take a photo before clocking');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (captureModal.useCode) {
        // Using employee code
        response = await axios.post(`${API}/supervisor/clock`, {
          employee_code: employeeCode,
          event_type: captureModal.eventType
        });
      } else {
        // Using direct employee button
        response = await axios.post(`${API}/supervisor/clock`, {
          employee_code: captureModal.employee.employee_code,
          event_type: captureModal.eventType,
          notes: comments[captureModal.employee.id] || ''
        });
      }

      // Upload photo
      await axios.post(`${API}/clock/photo`, {
        event_id: response.data.id,
        photo_base64: captureModal.photo
      });

      toast.success(response.data.message || `${captureModal.employee?.name || 'Employee'} clocked ${captureModal.eventType === 'clock_in' ? 'in' : 'out'}`);
      
      setRecentEvents(prev => [{
        ...response.data,
        photo_url: captureModal.photo,
        timestamp: new Date().toISOString()
      }, ...prev].slice(0, 10));

      if (captureModal.useCode) {
        setEmployeeCode('');
      } else {
        setComments(prev => ({ ...prev, [captureModal.employee.id]: '' }));
      }
      
      setCaptureModal({ open: false, employee: null, eventType: null, photo: null, useCode: false });
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Clock event failed');
    } finally {
      setLoading(false);
      setClockingEmployee(null);
    }
  };

  // Open capture modal for direct clock in (from staff list)
  const handleDirectClockIn = async (employee) => {
    setCaptureModal({
      open: true,
      employee: employee,
      eventType: 'clock_in',
      photo: null,
      useCode: false
    });
  };

  // Open capture modal for direct clock out (from staff list)
  const handleDirectClockOut = async (employee) => {
    setCaptureModal({
      open: true,
      employee: employee,
      eventType: 'clock_out',
      photo: null,
      useCode: false
    });
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  const toggleEmailVisibility = (id) => {
    setVisibleEmails(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleViewPhoto = async (member) => {
    setPhotoModal({ open: true, photo: null, name: member.name, timestamp: null, loading: true });
    
    try {
      const response = await axios.get(`${API}/clock/photo/${member.id}`);
      setPhotoModal(prev => ({
        ...prev,
        photo: response.data.photo_url,
        timestamp: response.data.timestamp,
        eventType: response.data.event_type,
        location: response.data.location,
        loading: false
      }));
    } catch (error) {
      setPhotoModal(prev => ({ ...prev, loading: false }));
      toast.error('Failed to load photo');
    }
  };

  const handleViewEventPhoto = async (event) => {
    setPhotoModal({ open: true, photo: null, name: event.employee_name, timestamp: event.timestamp, loading: true });
    try {
      const response = await axios.get(`${API}/admin/clock/photo/${event.id}`);
      if (response.data.photo_url && response.data.photo_url !== 'has_photo') {
        setPhotoModal(prev => ({ ...prev, photo: response.data.photo_url, loading: false }));
      } else {
        setPhotoModal(prev => ({ ...prev, loading: false }));
        toast.error('No photo available');
      }
    } catch (error) {
      setPhotoModal(prev => ({ ...prev, loading: false }));
      toast.error('Failed to load photo');
    }
  };

  // Combine employees and supervisors
  const allStaff = [
    ...employees.map(e => ({ ...e, role: 'employee' })),
    ...supervisors.map(s => ({ ...s, role: 'supervisor' }))
  ];

  // Filter by search term
  const filteredStaff = allStaff.filter(member => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return member.name?.toLowerCase().includes(search) || 
           member.email?.toLowerCase().includes(search);
  });

  return (
    <SupervisorLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center border-b border-border pb-6">
          <h1 className="text-3xl font-barlow tracking-widest text-primary mb-2">
            CLOCK IN / OUT
          </h1>
          <p className="text-muted-foreground">
            Enter employee code to clock them in or out
          </p>
        </div>

        {/* Pick Up Shift Section */}
        <Card className="bg-gradient-to-r from-blue-500/10 to-primary/10 border-blue-500/30 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-sm">
                  <CalendarPlus className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Pick Up a Shift</p>
                  <p className="text-xs text-muted-foreground">
                    {availableShiftsCount > 0 
                      ? `${availableShiftsCount} shift${availableShiftsCount > 1 ? 's' : ''} available for staff`
                      : 'No shifts available'}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/supervisor/upcoming-shifts')}
                data-testid="pick-up-shift-btn"
                className="bg-blue-500 hover:bg-blue-600 text-foreground rounded-sm"
              >
                Manage Shifts
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Clock In/Out Card */}
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-8">
            <div className="flex flex-col items-center space-y-6">
              {/* Code Input */}
              <div className="w-full max-w-xs space-y-2">
                <Label htmlFor="employee-code" className="text-center block text-sm uppercase tracking-wider text-muted-foreground">
                  Employee Code
                </Label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="employee-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={employeeCode}
                    onChange={(e) => setEmployeeCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="0000"
                    className="pl-10 text-center text-4xl font-mono tracking-[0.5em] h-16 bg-background border-border rounded-sm"
                    data-testid="employee-code-input"
                    autoFocus
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-4 w-full max-w-md">
                <Button
                  onClick={handleClockIn}
                  onKeyPress={(e) => handleKeyPress(e, handleClockIn)}
                  disabled={loading || employeeCode.length !== 4}
                  className="flex-1 h-16 bg-green-600 hover:bg-green-500 text-foreground rounded-sm text-lg font-barlow tracking-wider"
                  data-testid="clock-in-btn"
                >
                  <ArrowUpRight className="w-6 h-6 mr-2" />
                  CLOCK IN
                </Button>
                <Button
                  onClick={handleClockOut}
                  onKeyPress={(e) => handleKeyPress(e, handleClockOut)}
                  disabled={loading || employeeCode.length !== 4}
                  className="flex-1 h-16 bg-red-600 hover:bg-red-500 text-foreground rounded-sm text-lg font-barlow tracking-wider"
                  data-testid="clock-out-btn"
                >
                  <ArrowDownRight className="w-6 h-6 mr-2" />
                  CLOCK OUT
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Events */}
        {recentEvents.length > 0 && (
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="border-b border-border pb-4">
              <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2">
                {recentEvents.map((event, index) => (
                  <div 
                    key={index}
                    className={cn(
                      "p-3 rounded-sm",
                      event.event_type === 'clock_in' ? "bg-green-900/20 border border-green-900/50" : "bg-red-900/20 border border-red-900/50"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {event.event_type === 'clock_in' ? (
                          <ArrowUpRight className="w-5 h-5 text-green-400" />
                        ) : (
                          <ArrowDownRight className="w-5 h-5 text-red-400" />
                        )}
                        <span className="font-medium">{event.employee_name}</span>
                        <Badge className={event.event_type === 'clock_in' ? "bg-green-600" : "bg-red-600"}>
                          {event.event_type === 'clock_in' ? 'Clocked In' : 'Clocked Out'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground font-mono">
                          {formatUK(event.timestamp, 'HH:mm')}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className={`h-7 px-2 ${event.photo_url ? 'border-blue-500 text-blue-400 hover:bg-blue-500/10' : 'border-border text-muted-foreground/40'}`}
                          onClick={() => handleViewEventPhoto(event)}
                        >
                          <Camera className="w-3 h-3 mr-1" />
                          Photo
                        </Button>
                      </div>
                    </div>
                    {/* Show late reason or auto clock-out warning */}
                    {event.auto_clock_out && (
                      <div className="mt-2 p-2 bg-red-900/30 rounded-sm border-l-2 border-red-500">
                        <p className="text-xs text-red-400">⚠️ Auto Clock-Out: Employee did not clock out within 1 hour of shift end</p>
                      </div>
                    )}
                    {event.late_reason && !event.auto_clock_out && (
                      <div className="mt-2 p-2 bg-amber-900/30 rounded-sm border-l-2 border-amber-500">
                        <p className="text-xs text-amber-400">⚠️ Late {event.event_type === 'clock_in' ? 'Clock In' : 'Clock Out'}: {event.late_reason}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Employee Status List with Comments */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              Staff ({allStaff.length})
            </CardTitle>
            {/* Search Bar */}
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-background border-border rounded-sm"
                data-testid="search-staff-input"
              />
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {filteredStaff.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {searchTerm ? 'No results found' : 'No staff found'}
              </p>
            ) : (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                {filteredStaff.map((member) => (
                  <div 
                    key={member.id}
                    className="p-4 bg-background rounded-sm border border-border"
                  >
                    {/* Employee Info Row */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-3 h-3 rounded-full",
                          member.role === 'supervisor' 
                            ? "bg-blue-500"
                            : member.is_on_shift ? "bg-green-500" : "bg-secondary"
                        )} />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-lg">{member.name}</span>
                            <Badge className={cn(
                              "text-xs",
                              member.role === 'supervisor' 
                                ? "bg-blue-600/10 text-blue-400 border-blue-900"
                                : "bg-muted text-muted-foreground border-border"
                            )}>
                              {member.role === 'supervisor' ? (
                                <><Shield className="w-3 h-3 mr-1" />Supervisor</>
                              ) : (
                                'Employee'
                              )}
                            </Badge>
                          </div>
                          {/* Email - Show/Hide */}
                          <div className="flex items-center gap-1 mt-0.5">
                            {visibleEmails[member.id] ? (
                              <span className="text-xs text-muted-foreground">{member.email}</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">••••••••@••••</span>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleEmailVisibility(member.id)}
                              className="h-5 w-5 p-0 hover:bg-accent"
                              title={visibleEmails[member.id] ? "Hide email" : "Show email"}
                            >
                              {visibleEmails[member.id] ? (
                                <EyeOff className="w-3 h-3 text-muted-foreground" />
                              ) : (
                                <Eye className="w-3 h-3 text-muted-foreground" />
                              )}
                            </Button>
                            {/* View Photo Button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewPhoto(member)}
                              className="h-5 w-5 p-0 hover:bg-accent"
                              title="View clock-in photo"
                            >
                              <Camera className="w-3 h-3 text-muted-foreground" />
                            </Button>
                          </div>
                        </div>
                        {member.role === 'employee' && (
                          <span className="font-mono text-primary bg-primary/10 px-2 py-0.5 rounded text-sm">
                            #{member.employee_code || '----'}
                          </span>
                        )}
                      </div>
                      {member.role === 'employee' && (
                        member.is_on_shift ? (
                          <div className="flex flex-col items-end gap-0.5">
                            <Badge className="bg-green-600">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              On Shift
                            </Badge>
                            {member.last_clock_event && (
                              <span className="text-xs text-green-400">
                                Since {formatUK(member.last_clock_event, 'h:mm a')}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-end gap-0.5">
                            <Badge variant="outline" className="border-border text-muted-foreground">
                              <XCircle className="w-3 h-3 mr-1" />
                              Off
                            </Badge>
                            {member.last_clock_event && (
                              <span className="text-xs text-muted-foreground">
                                Last: {formatUK(member.last_clock_event, 'h:mm a')}
                              </span>
                            )}
                          </div>
                        )
                      )}
                    </div>
                    
                    {/* Comment Box and Clock Buttons - Only for employees */}
                    {member.role === 'employee' && (
                      <>
                        {/* Comment Box */}
                        <div className="mb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <MessageSquare className="w-4 h-4 text-muted-foreground" />
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                              Comment / Note
                            </Label>
                          </div>
                          <Textarea
                            placeholder="Add a comment for this clock event..."
                            value={comments[member.id] || ''}
                            onChange={(e) => handleCommentChange(member.id, e.target.value)}
                            className="bg-muted border-border rounded-sm min-h-[60px] text-sm"
                            data-testid={`comment-${member.id}`}
                          />
                        </div>
                        
                        {/* Clock Buttons */}
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleDirectClockIn(member)}
                            disabled={clockingEmployee === member.id || member.is_on_shift}
                            className={cn(
                              "flex-1 h-12 rounded-sm font-bold",
                              member.is_on_shift 
                                ? "bg-accent text-muted-foreground cursor-not-allowed" 
                                : "bg-green-600 hover:bg-green-500 text-foreground"
                            )}
                            data-testid={`clock-in-${member.id}`}
                          >
                            <ArrowUpRight className="w-5 h-5 mr-2" />
                            CLOCK IN
                          </Button>
                          <Button
                            onClick={() => handleDirectClockOut(member)}
                            disabled={clockingEmployee === member.id || !member.is_on_shift}
                            className={cn(
                              "flex-1 h-12 rounded-sm font-bold",
                              !member.is_on_shift 
                                ? "bg-accent text-muted-foreground cursor-not-allowed" 
                                : "bg-red-600 hover:bg-red-500 text-foreground"
                            )}
                            data-testid={`clock-out-${member.id}`}
                          >
                            <ArrowDownRight className="w-5 h-5 mr-2" />
                            CLOCK OUT
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Photo Modal */}
        <Dialog open={photoModal.open} onOpenChange={(open) => setPhotoModal(prev => ({ ...prev, open }))}>
          <DialogContent className="bg-card border-border rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                Clock-In Photo
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground text-center">{photoModal.name}</p>
              
              {photoModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : photoModal.photo ? (
                <div className="space-y-3">
                  <div className="relative aspect-video bg-muted rounded-sm overflow-hidden">
                    <img 
                      src={photoModal.photo} 
                      alt="Clock-in photo" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-center space-y-1">
                    {photoModal.timestamp && (
                      <p className="text-xs text-muted-foreground">
                        📅 {formatUK(photoModal.timestamp, "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    )}
                    {photoModal.location && (
                      <p className="text-xs text-muted-foreground">
                        📍 {photoModal.location}
                      </p>
                    )}
                    {photoModal.eventType && (
                      <Badge className={cn(
                        "text-xs",
                        photoModal.eventType === 'clock_in' 
                          ? "bg-green-600/10 text-green-400" 
                          : "bg-red-600/10 text-red-400"
                      )}>
                        {photoModal.eventType === 'clock_in' ? 'Clock In' : 'Clock Out'}
                      </Badge>
                    )}
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center">
                  <Camera className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">No photo available</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    This staff member hasn't uploaded a clock-in photo yet
                  </p>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPhotoModal({ open: false, photo: null, name: '', timestamp: null, loading: false })}
                  className="flex-1 border-border rounded-sm"
                >
                  Close
                </Button>
                {photoModal.photo && (
                  <Button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = photoModal.photo;
                      const fileName = `${photoModal.name.replace(/\s+/g, '_')}_${photoModal.eventType || 'clock'}_${photoModal.timestamp ? formatUK(photoModal.timestamp, 'yyyy-MM-dd_HHmm') : 'photo'}.jpg`;
                      link.download = fileName;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      toast.success('Photo downloaded');
                    }}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Save Photo
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Photo Capture Modal for Clock In/Out */}
        <Dialog open={captureModal.open} onOpenChange={(open) => {
          if (!open) {
            setCaptureModal({ open: false, employee: null, eventType: null, photo: null, useCode: false });
          }
        }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                {captureModal.eventType === 'clock_in' ? 'Clock In' : 'Clock Out'} - Photo Required
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground text-center">
                Take a photo to {captureModal.eventType === 'clock_in' ? 'clock in' : 'clock out'} 
                {captureModal.employee ? ` ${captureModal.employee.name}` : ''}
              </p>
              
              {/* Photo Preview or Capture */}
              {captureModal.photo ? (
                <div className="space-y-3">
                  <div className="relative aspect-video bg-muted rounded-sm overflow-hidden">
                    <img 
                      src={captureModal.photo} 
                      alt="Captured photo" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setCaptureModal(prev => ({ ...prev, photo: null }))}
                    className="w-full border-border rounded-sm"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Retake Photo
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <label 
                    htmlFor="photo-capture"
                    className="flex flex-col items-center justify-center aspect-video bg-muted rounded-sm border-2 border-dashed border-border cursor-pointer hover:border-primary/50 transition-colors"
                  >
                    <Camera className="w-12 h-12 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Tap to take photo</p>
                    <p className="text-xs text-muted-foreground mt-1">Photo is required</p>
                  </label>
                  <input
                    id="photo-capture"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoCapture}
                    className="hidden"
                  />
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCaptureModal({ open: false, employee: null, eventType: null, photo: null, useCode: false })}
                  className="flex-1 border-border rounded-sm"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={executeClockWithPhoto}
                  disabled={!captureModal.photo || loading}
                  className={cn(
                    "flex-1 rounded-sm",
                    captureModal.eventType === 'clock_in' 
                      ? "bg-green-600 hover:bg-green-500 text-foreground" 
                      : "bg-red-600 hover:bg-red-500 text-foreground",
                    !captureModal.photo && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {loading ? (
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  ) : captureModal.eventType === 'clock_in' ? (
                    <ArrowUpRight className="w-4 h-4 mr-2" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 mr-2" />
                  )}
                  {captureModal.eventType === 'clock_in' ? 'Clock In' : 'Clock Out'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </SupervisorLayout>
  );
}
